import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, Scene } from 'react-scrollmagic'
import PropTypes from 'prop-types'

import { Container, Slide } from './styles'

// A sequence is a folder of frames the scrubber swaps between: every frame is
// in the DOM at once and all but the current one are `display: none`. Browsers
// fetch `display: none` images, so mounting all six of /blueprint's sequences
// up front downloaded 231 frames — 3.8 MB, the largest page weight on the site
// — before the visitor had scrolled anywhere near most of them.
//
// The frames are NOT oversized, so shrinking them is the wrong fix: measured,
// the box renders 380 CSS px wide at DPR 2.625, which needs ~998 px, and the
// sources are 1080 px. Downscaling would show on exactly the devices that can
// least afford the bytes. So postpone the download instead of degrading it.
//
// ROOT_MARGIN arms a sequence a viewport and a half before it scrolls into
// view — far more runway than the scrub needs — and a sequence never disarms
// once armed. Swapping frame sets is layout-neutral because every frame is
// `position: absolute` inside a holder with an explicit height.
//
// Honest accounting: on a tall desktop viewport this margin already reaches
// `what`, `step01` and `step02` from the top of the page, so ~127 of the 231
// frames still load without scrolling. The saving there is ~1.1 MB, not the
// full 3.8 MB; on a phone, where the margin covers much less document, it is
// most of it.
const ROOT_MARGIN = '150% 0px'

function getSlidesArray(id, totalSlidesCount, path) {
  const array = []
  for (let i = 0; i < totalSlidesCount; i += 1) {
    array.push({ id: `${id}Slide${i}`, path: `${path + (100 + i)}.webp` })
  }
  return array
}

function getSlideIndex(progress, totalSlidesCount) {
  return Math.round(progress * 100 * ((totalSlidesCount - 1) / 100))
}

function getSlides(slidesArray, slideIndex) {
  return (
    <div>
      {slidesArray.map((slide, index) => (
        <Slide
          // Frames of a scroll-driven animation: decorative, so an empty alt
          // is the honest value. Without the attribute at all they failed
          // `image-alt` in both the a11y and SEO categories.
          alt=""
          key={slide.path}
          src={slide.path}
          visible={slideIndex === index}
        />
      ))}
    </div>
  )
}

function AnimatedScrollBox({ sequence }) {
  const { id, path, totalSlidesCount, duration, trigger } = sequence

  const containerRef = useRef(null)
  // Starts false on the server and on the first client render alike, so the
  // deferral cannot produce a hydration mismatch.
  const [armed, setArmed] = useState(false)

  const slidesArray = useMemo(
    () => getSlidesArray(id, totalSlidesCount, path),
    [id, path, totalSlidesCount],
  )

  useEffect(() => {
    if (armed) return undefined

    const node = containerRef.current
    if (!node) return undefined

    // Without IntersectionObserver, keep the old behaviour rather than risk a
    // sequence that never renders.
    if (typeof IntersectionObserver === 'undefined') {
      setArmed(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) setArmed(true)
      },
      { rootMargin: ROOT_MARGIN },
    )
    observer.observe(node)

    return () => observer.disconnect()
  }, [armed])

  return (
    <Container ref={containerRef}>
      <Controller>
        <Scene duration={`${duration}%`} triggerHook={trigger}>
          {progress => {
            // Before arming, still render frame 0. It is what the server
            // prerenders and what the first client render produces, so
            // hydration matches; it also means a visitor with JS disabled, or
            // printing the page, sees the illustration instead of an empty
            // box. Six frames sitewide rather than 231.
            if (!armed) return getSlides(slidesArray.slice(0, 1), 0)

            const slideIndex = getSlideIndex(progress, totalSlidesCount)
            return getSlides(slidesArray, slideIndex)
          }}
        </Scene>
      </Controller>
    </Container>
  )
}

AnimatedScrollBox.propTypes = {
  sequence: PropTypes.object.isRequired,
}

export default AnimatedScrollBox
